import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Mail, Clock } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Button from "@/components/Button";
import { CONTACT, waLink } from "@/data/site";
import { easeLux } from "@/lib/motion";
import Seo from "@/components/Seo";

const Contacto = () => {
  const [form, setForm] = useState({ nombre: "", correo: "", mensaje: "" });

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.mensaje.trim()) {
      toast.error("Por favor completa tu nombre y mensaje.");
      return;
    }
    const message =
      `Hola Lu Design 👋\n\n` +
      `Soy ${form.nombre}.` +
      (form.correo ? ` Mi correo es ${form.correo}.` : "") +
      `\n\n${form.mensaje}`;
    window.open(waLink(message), "_blank", "noopener,noreferrer");
    toast.success("Abriendo WhatsApp para enviar tu mensaje…");
  };

  const details = [
    { icon: MapPin, label: "Ubicación", value: CONTACT.location },
    { icon: Mail, label: "Correo", value: CONTACT.email },
    { icon: Clock, label: "Horario", value: "Lun a Vie · 9:00 – 18:00" },
  ];

  return (
    <div data-testid="contacto-page">
      <Seo
        title="Contacto"
        description="¿Tienes un proyecto en mente? Escríbeme por WhatsApp. Lu Design te acompaña en diseño web, invitaciones digitales, diseño gráfico y fotografía."
        path="/contacto"
      />
      <PageHero
        overline="Contacto"
        titleLines={["Escribamos", "algo hermoso."]}
        intro="Cuéntame sobre tu proyecto, tu evento o esa idea que traes en mente. Respondo por WhatsApp para conocernos y comenzar tu historia."
        testId="contacto-hero"
      />

      <section className="relative z-10 bg-powder/60 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-10 lg:grid-cols-5 lg:gap-16">
            {/* Formulario */}
            <Reveal className="lg:col-span-3" amount={0.15}>
              <div className="bg-ivory p-8 md:p-12 shadow-none border border-rosegold/20">
                <h2 className="font-serif text-3xl md:text-4xl text-plum">Envíame un mensaje</h2>
                <p className="mt-3 font-sans font-light text-sm text-charcoal/60">
                  Al enviar, se abrirá WhatsApp con tu mensaje listo para conversar.
                </p>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="contact-form">
                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-charcoal/60">
                      Nombre
                    </label>
                    <input
                      name="nombre"
                      value={form.nombre}
                      onChange={update}
                      data-testid="contact-name"
                      placeholder="Tu nombre"
                      className="w-full border-b border-rosegold/40 bg-transparent py-3 font-sans font-light text-charcoal placeholder:text-charcoal/30 outline-none transition-colors duration-300 focus:border-plum"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-charcoal/60">
                      Correo <span className="normal-case tracking-normal text-charcoal/30">(opcional)</span>
                    </label>
                    <input
                      name="correo"
                      type="email"
                      value={form.correo}
                      onChange={update}
                      data-testid="contact-email"
                      placeholder="tucorreo@ejemplo.com"
                      className="w-full border-b border-rosegold/40 bg-transparent py-3 font-sans font-light text-charcoal placeholder:text-charcoal/30 outline-none transition-colors duration-300 focus:border-plum"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-charcoal/60">
                      Mensaje
                    </label>
                    <textarea
                      name="mensaje"
                      value={form.mensaje}
                      onChange={update}
                      data-testid="contact-message"
                      rows={4}
                      placeholder="Cuéntame sobre tu proyecto…"
                      className="w-full resize-none border-b border-rosegold/40 bg-transparent py-3 font-sans font-light text-charcoal placeholder:text-charcoal/30 outline-none transition-colors duration-300 focus:border-plum"
                    />
                  </div>
                  <Button type="submit" onClick={undefined} testId="contact-submit" className="w-full sm:w-auto">
                    Enviar por WhatsApp
                  </Button>
                </form>
              </div>
            </Reveal>

            {/* Datos + redes */}
            <div className="lg:col-span-2 flex flex-col justify-between gap-10">
              <Reveal amount={0.15}>
                <div className="bg-plum p-8 md:p-10 text-ivory">
                  <p className="text-xs uppercase tracking-[0.3em] text-rosegold">Directo</p>
                  <h3 className="mt-4 font-serif text-3xl md:text-4xl">Hablemos por WhatsApp</h3>
                  <p className="mt-4 font-sans font-light text-sm leading-relaxed text-ivory/70">
                    La forma más rápida de comenzar. Escríbeme y te respondo personalmente.
                  </p>
                  <a
                    href={waLink("Hola Lu Design, me gustaría platicar sobre un proyecto.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-cta"
                    className="group mt-8 inline-flex items-center gap-3 rounded-full bg-rosegold px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-ivory transition-[background-color,transform] duration-500 hover:-translate-y-0.5 hover:bg-ivory hover:text-plum"
                  >
                    <i className="fa-brands fa-whatsapp text-lg" />
                    {CONTACT.whatsappDisplay}
                  </a>
                </div>
              </Reveal>

              <Reveal amount={0.15} delay={0.1}>
                <div className="space-y-6">
                  {details.map((d) => (
                    <div key={d.label} className="flex items-start gap-4">
                      <d.icon className="mt-1 h-5 w-5 text-rosegold" strokeWidth={1.25} />
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-charcoal/50">{d.label}</p>
                        <p className="mt-1 font-sans font-light text-charcoal/80">{d.value}</p>
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-4 pt-2">
                    {CONTACT.socials.map((s) => (
                      <a
                        key={s.name}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.name}
                        data-testid={`contact-social-${s.name.toLowerCase()}`}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-plum/30 text-plum transition-[background-color,color,border-color] duration-500 hover:border-plum hover:bg-plum hover:text-ivory"
                      >
                        <i className={`${s.icon} text-lg`} />
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
